import {
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  HStack,
  Icon,
  ScaleFade,
  Text,
  VStack,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  useColorModeValue,
} from "@chakra-ui/react";
import { brandPalette } from "../../constants/brandPalette";
import { Field, Form, Formik } from "formik";
import React, { useEffect, useState, useMemo } from "react";
import { BiLowVision, BiShow } from "react-icons/bi";
import { SearchIcon, ArrowLeftIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import { FaBookOpen, FaLock, FaPhone } from "react-icons/fa";
import { FiShield } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { getAllSubjectsRequest } from "../../modules/admin-modules/subjects/Actions";
import { loginUserAction } from "../../modules/auth/Actions";
import PublicLayout from "../public/Layout/layout";

const LoginForm = (props) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.auth.isLoading);
  const [inputType, setInputType] = useState("password");
  const subjects = useSelector((state) => state.subjects.subjects);
  const [currentStep, setCurrentStep] = useState(1); // 1: Subject selection, 2: Login form
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    dispatch(getAllSubjectsRequest());
  }, []);

  const palette = {
    primary: brandPalette.primary,
    secondary: brandPalette.secondary,
    tertiary: brandPalette.stone,
    neutral: brandPalette.cream,
    ink: "#2D2A26",
    soft: "#6B5A5A",
    tanTagline: "#B8956C",
    muted: "#718096",
    inputBorder: "#E2DDD8",
  };

  const serifHeading = "'Georgia', 'Times New Roman', serif";
  const sansUi =
    "'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif";

  // Filter subjects based on search
  const filteredSubjects = useMemo(() => {
    const safeSubjects = Array.isArray(subjects) ? subjects : [];
    return safeSubjects.filter((subject) => {
      const matchesSearch = subject.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [subjects, searchTerm]);

  const validateUserName = (value) => {
    let error;
    if (!value) {
      error = "Required";
    }
    return error;
  };

  const validatePassword = (value) => {
    let error;
    if (!value) {
      error = "Required";
    }
    return error;
  };

  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    setCurrentStep(2);
  };

  const handleBackToSubjects = () => {
    setCurrentStep(1);
    setSelectedSubject(null);
  };

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const baseUrl = "https://api.edutopiaonline.com/";

  // Step 1: Subject Selection
  const renderSubjectSelection = () => (
    <VStack spacing={8} align="stretch" w="100%">
      <VStack spacing={4} align="center">
        <Heading 
          textAlign="center" 
          mb="6"
          fontSize={{ base: "28px", md: "36px" }}
          fontWeight="800"
          color={brandPalette.secondary}
          fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
        >
          Choose the subject
        </Heading>

      </VStack>
      
      {/* Search Bar */}
      <Box>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color={palette.primary} />
          </InputLeftElement>
          <Input
            placeholder="Search for the subject..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="lg"
            borderRadius="xl"
            border="1px"
            borderColor={palette.tertiary}
            bg="rgba(255, 255, 255, 0.72)"
            backdropFilter="blur(12px)"
            _focus={{
              borderColor: palette.primary,
              boxShadow: "0 0 0 3px rgba(91, 13, 22, 0.12)",
            }}
            _hover={{
              borderColor: palette.primary,
            }}
            fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
          />
        </InputGroup>
      </Box>

      {/* Results Count */}
      <Text 
        fontSize="sm" 
        color={palette.soft}
        fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
      >
        Found {filteredSubjects.length} subjects
      </Text>

      {/* Subject List */}
      <Box>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {filteredSubjects.map((subject) => (
            <Button
              key={subject.id}
              onClick={() => handleSubjectSelect(subject)}
              variant="outline"
              height="auto"
              p={8}
              textAlign="left"
              borderRadius="xl"
              borderWidth="1px"
              borderColor={palette.tertiary}
              bg="rgba(255, 255, 255, 0.72)"
              backdropFilter="blur(12px)"
              _hover={{
                borderColor: palette.primary,
                bg: "rgba(255, 255, 255, 0.88)",
                shadow: "0 14px 30px rgba(69, 11, 17, 0.12)",
                transform: "translateY(-3px)",
              }}
              _active={{
                transform: 'scale(0.98)',
              }}
              transition="all 0.3s ease"
              boxShadow="0 4px 15px rgba(69, 11, 17, 0.08)"
            >
              <Flex justify="space-between" align="flex-start" width="100%">
                <VStack align="start" spacing={3} flex={1} minW={0}>
                  <Text 
                    fontSize="lg" 
                    fontWeight="700" 
                    wordBreak="break-word" 
                    width="100%" 
                    whiteSpace="normal" 
                    overflowWrap="break-word"
                    color={palette.secondary}
                    fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                    lineHeight="1.4"
                  >
                    {subject.name}
                  </Text>
                  <Text 
                    fontSize="sm" 
                    color={palette.soft}
                    fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                  >
                    Press to select
                  </Text>
                </VStack>

                {subject.logo ? (
                  <Box
                    width="90px"
                    height="90px"
                    borderRadius="xl"
                    overflow="hidden"
                    bg="rgba(243, 236, 230, 0.9)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    ml={4}
                    flexShrink={0}
                    border="1px solid"
                    borderColor={palette.tertiary}
                    boxShadow="0 4px 15px rgba(69, 11, 17, 0.08)"
                  >
                    <img
                      src={baseUrl + subject.logo}
                      alt={subject.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                ) : (
                  <Box
                    width="90px"
                    height="90px"
                    borderRadius="xl"
                    overflow="hidden"
                    bg="rgba(233, 226, 219, 0.9)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    ml={4}
                    flexShrink={0}
                    border="1px solid"
                    borderColor={palette.tertiary}
                    boxShadow="0 4px 15px rgba(69, 11, 17, 0.08)"
                  >
                    <img
                      src={"/logo.png"}
                      alt={subject.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                )}
              </Flex>
            </Button>
          ))}
        </SimpleGrid>

        {filteredSubjects.length === 0 && (
          <VStack py={16} spacing={6}>
            <Icon as={FaBookOpen} boxSize={16} color={palette.primary} />
            <Text 
              fontSize="xl" 
              fontWeight="700" 
              color={palette.secondary}
              fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
            >
              No subjects found
            </Text>
            <Text 
              color={palette.soft}
              textAlign="center"
              fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
            >
              Try modifying the search or filtering criteria
            </Text>
          </VStack>
        )}
      </Box>
    </VStack>
  );

  const inputFieldStyles = {
    size: "lg",
    borderRadius: "lg",
    border: "1px solid",
    borderColor: palette.inputBorder,
    bg: "white",
    pl: "44px",
    fontFamily: sansUi,
    _focus: {
      borderColor: palette.primary,
      boxShadow: "0 0 0 1px rgba(91, 13, 22, 0.2)",
    },
    _hover: {
      borderColor: palette.tertiary,
    },
  };

  // Step 2: Login Form (card layout aligned with brand login screen)
  const renderLoginForm = () => (
    <Formik
      initialValues={{
        phone: "",
        password: "",
        subject_id: selectedSubject?.id || null,
      }}
      onSubmit={(values) => {
        let formData = new FormData();
        formData.append("phone", values.phone);
        formData.append("password", values.password);
        formData.append("subject_id", values.subject_id);

        dispatch(loginUserAction(formData));
      }}
    >
      {({ errors, touched, values, setFieldValue }) => {
        if (selectedSubject && values.subject_id !== selectedSubject.id) {
          setFieldValue("subject_id", selectedSubject.id);
        }

        return (
          <Form>
            <VStack spacing={6} align="stretch" w="100%">
              <Button
                alignSelf="flex-start"
                leftIcon={<ArrowLeftIcon />}
                variant="ghost"
                onClick={handleBackToSubjects}
                color={palette.soft}
                size="sm"
                px={2}
                fontWeight="600"
                fontFamily={sansUi}
                _hover={{
                  bg: "rgba(255, 255, 255, 0.65)",
                  color: palette.primary,
                }}
              >
                Return to subjects
              </Button>

              <Box
                bg="white"
                borderRadius="2xl"
                boxShadow="0 8px 32px rgba(45, 42, 38, 0.08)"
                border="1px solid"
                borderColor="rgba(214, 206, 192, 0.55)"
                px={{ base: 6, md: 10 }}
                py={{ base: 8, md: 10 }}
              >
                <VStack spacing={6} align="stretch">
                  <Box textAlign="center">
                    <Heading
                      as="h2"
                      fontSize={{ base: "2xl", md: "3xl" }}
                      fontWeight="700"
                      color={palette.primary}
                      fontFamily={serifHeading}
                      letterSpacing="-0.02em"
                      mb={2}
                    >
                      Welcome Back
                    </Heading>
                    <Text
                      fontSize="sm"
                      color={palette.muted}
                      fontFamily={sansUi}
                      mb={1}
                    >
                      Continue your learning journey.
                    </Text>
                    {selectedSubject && (
                      <Text
                        fontSize="xs"
                        color={palette.tanTagline}
                        fontFamily={sansUi}
                        letterSpacing="0.06em"
                        textTransform="uppercase"
                        fontWeight="600"
                      >
                        {selectedSubject.name}
                      </Text>
                    )}
                  </Box>

                  <Box>
                    <FormLabel
                      htmlFor="phone"
                      fontSize="sm"
                      fontWeight="700"
                      color={palette.ink}
                      mb={2}
                      fontFamily={sansUi}
                    >
                      Phone
                    </FormLabel>
                    <Field name="phone" validate={validateUserName}>
                      {({ field }) => (
                        <InputGroup size="lg">
                          <InputLeftElement h="full" pointerEvents="none">
                            <Icon
                              as={FaPhone}
                              color={palette.soft}
                              boxSize={5}
                            />
                          </InputLeftElement>
                          <Input
                            {...field}
                            id="phone"
                            placeholder="Enter your phone number"
                            {...inputFieldStyles}
                          />
                        </InputGroup>
                      )}
                    </Field>
                    {errors.phone && touched.phone && (
                      <Text
                        color="red.500"
                        fontSize="sm"
                        mt={2}
                        fontFamily={sansUi}
                      >
                        {errors.phone}
                      </Text>
                    )}
                  </Box>

                  <Box>
                    <FormLabel
                      htmlFor="password"
                      fontSize="sm"
                      fontWeight="700"
                      color={palette.ink}
                      mb={2}
                      fontFamily={sansUi}
                    >
                      Password
                    </FormLabel>
                    <Field name="password" validate={validatePassword}>
                      {({ field }) => (
                        <InputGroup size="lg">
                          <InputLeftElement h="full" pointerEvents="none">
                            <Icon
                              as={FaLock}
                              color={palette.soft}
                              boxSize={5}
                            />
                          </InputLeftElement>
                          <Input
                            {...field}
                            id="password"
                            type={inputType}
                            placeholder="Enter your password"
                            {...inputFieldStyles}
                            pr="44px"
                          />
                          <InputRightElement h="full" width="44px">
                            <Icon
                              as={inputType === "password" ? BiShow : BiLowVision}
                              fontSize="20px"
                              color={palette.soft}
                              cursor="pointer"
                              _hover={{ color: palette.primary }}
                              onClick={() =>
                                setInputType(
                                  inputType === "password" ? "text" : "password"
                                )
                              }
                              aria-label={
                                inputType === "password"
                                  ? "Show password"
                                  : "Hide password"
                              }
                            />
                          </InputRightElement>
                        </InputGroup>
                      )}
                    </Field>
                    {errors.password && touched.password && (
                      <Text
                        color="red.500"
                        fontSize="sm"
                        mt={2}
                        fontFamily={sansUi}
                      >
                        {errors.password}
                      </Text>
                    )}
                  </Box>

                  <Button
                    type="submit"
                    w="full"
                    h="52px"
                    isLoading={isLoading}
                    loadingText="Logging in"
                    bg={palette.primary}
                    color="white"
                    borderRadius="lg"
                    fontSize="md"
                    fontWeight="700"
                    fontFamily={sansUi}
                    rightIcon={<ArrowForwardIcon />}
                    _hover={{
                      bg: palette.secondary,
                      boxShadow: "0 10px 28px rgba(91, 13, 22, 0.28)",
                    }}
                    _active={{ transform: "scale(0.99)" }}
                    transition="all 0.2s ease"
                  >
                    Log In
                  </Button>
                </VStack>
              </Box>

              <HStack
                justify="center"
                spacing={2}
                color={palette.muted}
                pt={1}
              >
                <Icon as={FiShield} boxSize={4} />
                <Text fontSize="xs" fontFamily={sansUi}>
                  Your data is secure with us
                </Text>
              </HStack>
            </VStack>
          </Form>
        );
      }}
    </Formik>
  );

  return (
    <PublicLayout color="main_1" bg="white" register={false}>
      <Flex
        flexDirection="column"
        alignItems="center"
        my="50px"
        w="100%"
        h="100%"
      >
        <Box
          sx={{ 
            width: { base: "95%", md: currentStep === 1 ? "90%" : "40%" },
            maxWidth: currentStep === 1 ? "1200px" : "500px"
          }}
          transition="all 260ms ease"
        >
          <ScaleFade initialScale={0.98} in={currentStep === 1} unmountOnExit>
            <Box>{renderSubjectSelection()}</Box>
          </ScaleFade>

          <ScaleFade initialScale={0.98} in={currentStep === 2} unmountOnExit>
            <Box>{renderLoginForm()}</Box>
          </ScaleFade>
        </Box>
      </Flex>
    </PublicLayout>
  );
};

export default LoginForm;
